import React, { useCallback, useContext, useState } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { Route, selectRoute } from "src/config/routes";
import { useLocation } from "react-router-dom";
import styles from "./AssemblyLiveModeBanner.module.css";
import liveModeStyles from "src/screens/assemblies/assembly/livemode/LiveMode.module.css";
import { useHistory } from "react-router-dom";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { LOADING_TYPE } from "src/stores/assemblies/AssemblyStore";
import saveFormModalStyles from "src/hooks/save-unsaved-changes-in-form/useSaveUnsavedChangesInForm.module.css";

const AssemblyLiveModeBannerBase = () => {
    const { t } = useTranslation();
    const { assemblyStore, authStore, agendaitemStore, navStore, uiStore } = useContext(RootStoreContext);
    const location = useLocation();
    const history = useHistory();

    const [isConfirmModalDisplayed, setIsConfirmModalDisplayed] = useState<boolean>(false);

    const activateLiveMode = useCallback(async () => {
        await assemblyStore.updateAssemblyLiveMode(
            true,
            t("screens.assemblies.livemode.successfull_activate").toString()
        );
        // Always start on the attendance list, even if the last session ended on the close tab
        assemblyStore.setIsCloseLiveModeTabActive(false);
        assemblyStore.setIsAttendanceLiveModeTabActive(true);
        agendaitemStore.setCurrentAgendaItem(undefined);

        history.push(
            selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                assemblyid: assemblyStore.currentAssembly?.id || ""
            })
        );
    }, [assemblyStore, t, authStore.user?.role, history, agendaitemStore]);

    const onClickStartLiveMode = useCallback(async () => {
        if (agendaitemStore.agendaItemsHaveChanged) {
            setIsConfirmModalDisplayed(true);
            return;
        }

        await activateLiveMode();
    }, [agendaitemStore.agendaItemsHaveChanged, activateLiveMode]);

    const onSaveAndActivate = useCallback(async () => {
        await Promise.all([agendaitemStore.saveAgendaItems(), assemblyStore.saveAssemblyHeaderFooter()]);
        setIsConfirmModalDisplayed(false);
        await activateLiveMode();
    }, [agendaitemStore, assemblyStore, activateLiveMode]);

    const onDiscardAndActivate = useCallback(async () => {
        setIsConfirmModalDisplayed(false);
        // Reset the dirty flag so the <Prompt> in AgendaItemOverview does not block navigation
        agendaitemStore.setOriginalAgendaItems(agendaitemStore.currentAgendaItems);
        agendaitemStore.setAgendaItemsHaveChanged();
        await activateLiveMode();
    }, [agendaitemStore, activateLiveMode]);

    const onCancelConfirmModal = useCallback(() => {
        setIsConfirmModalDisplayed(false);
    }, []);

    const onClickStopLiveMode = useCallback(async () => {
        await assemblyStore.updateAssemblyLiveMode(
            false,
            t("screens.assemblies.livemode.successfull_closed").toString()
        );
        history.push(
            selectRoute(Route.agendaPreview, authStore.user?.role, {
                assemblyid: assemblyStore.currentAssembly?.id || ""
            })
        );
        assemblyStore.setIsAttendanceLiveModeTabActive(false);
        assemblyStore.stopSubscriptionHasAssemblyLiveModeChanged();
    }, [assemblyStore, authStore.user?.role, history, t]);

    const isSaving = assemblyStore.loadingType === LOADING_TYPE.SAVING_ASSEMBLY;

    const assemblyId = assemblyStore.currentAssembly?.id || "";
    const attendanceListRoute = Route.assemblyAttendanceList.replace(":assemblyid", assemblyId);
    const agendaPreviewRoute = Route.agendaPreview.replace(":assemblyid", assemblyId);
    const liveModeRoute = Route.assemblyLiveMode.replace(":assemblyid", assemblyId);

    const isAssemblyStateLiveModeActive = assemblyStore.currentAssembly?.livemode;
    const isPathnameAttendanceListOrAgendaPreviewRoute =
        location.pathname.includes(attendanceListRoute) || location.pathname.includes(agendaPreviewRoute);
    const isPathnameLiveModeRoute = location.pathname.includes(liveModeRoute);

    const shouldRenderAssemblyLiveModeBanner =
        navStore.hasFeatureFlagAssembly &&
        ((!isAssemblyStateLiveModeActive && isPathnameAttendanceListOrAgendaPreviewRoute) ||
            (isAssemblyStateLiveModeActive && isPathnameLiveModeRoute));

    if (!shouldRenderAssemblyLiveModeBanner || uiStore.isMobile) {
        return null;
    }

    return (
        <div className={styles.AssemblyLiveModeBanner}>
            <div className={`container ${styles.AssemblyLiveModeBannerContainer}`}>
                <UI.Row>
                    <UI.Col xl={12} sm={12} className={styles.AssemblyLiveModeRow}>
                        <div className={styles.AssemblyLiveModeInfo}>
                            <UI.Icon
                                icon={UI.SVGIcon.Information}
                                size={56}
                                className={styles.AssemblyLiveModeInfoIcon}
                            />
                        </div>
                        <div className={styles.AssemblyLiveModeText}>
                            {isPathnameLiveModeRoute
                                ? t("screens.assemblies.livemode.banner_text_stop")
                                : t("screens.assemblies.livemode.banner_text_start")}
                        </div>
                        {isPathnameLiveModeRoute ? (
                            <UI.Button
                                className={styles.AssemblyLiveModeStopButton}
                                label={
                                    <span className={liveModeStyles.StopButtonLabel}>
                                        <span className={liveModeStyles.StopButtonIcon} />
                                        <span>{t("screens.assemblies.livemode.pause_or_end_button").toString()}</span>
                                    </span>
                                }
                                onClick={onClickStopLiveMode}
                                variant="danger"
                            />
                        ) : (
                            <button onClick={onClickStartLiveMode} className={styles.AssemblyLiveModeButton}>
                                {uiStore.currentScreenSize > SCREEN_SIZE.TABLET ? (
                                    <span className={styles.AssemblyLiveModeButtonContent}>
                                        <UI.Icon
                                            icon={UI.SVGIcon.Vector}
                                            size={40}
                                            className={styles.AssemblyLiveModeButtonIcon}
                                        />
                                        <span className={styles.AssemblyLiveModeButtonLabel}>
                                            {t("screens.assemblies.livemode.start_button")}
                                        </span>
                                    </span>
                                ) : (
                                    <UI.Icon icon={UI.SVGIcon.Vector} size={40} />
                                )}
                            </button>
                        )}
                    </UI.Col>
                </UI.Row>
            </div>
            <UI.Modal show={isConfirmModalDisplayed} onClose={onCancelConfirmModal}>
                <div>
                    <div className={saveFormModalStyles.SaveFormModalText}>
                        <div>{t("form.has_unsaved_changes")}</div>
                        {isSaving ? (
                            <div className="d-flex align-items-center">
                                <span>{t("form.saving_changes")}</span>
                                <UI.RotatingSpinner noLogo size={20} className="loading-items-spinner" />
                            </div>
                        ) : (
                            <div style={{ height: "36px" }} />
                        )}
                    </div>
                    <div className={saveFormModalStyles.SaveFormModalButtonContainer}>
                        <UI.Button
                            label={t("labels.save")}
                            onClick={onSaveAndActivate}
                            variant="outline-primary"
                            disabled={isSaving}
                        />
                        <UI.Button
                            label={t("labels.discard")}
                            onClick={onDiscardAndActivate}
                            variant="outline-primary"
                            disabled={isSaving}
                        />
                        <UI.Button
                            label={t("labels.cancel")}
                            onClick={onCancelConfirmModal}
                            variant="outline-primary"
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </UI.Modal>
        </div>
    );
};

export const AssemblyLiveModeBanner = Sentry.withProfiler(observer(AssemblyLiveModeBannerBase));
